import React from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  Drawer,
  IconButton,
  Link,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import {
  hubspotBuildingUrl, hubspotContactUrl, hubspotDealUrl, hubspotTicketUrl,
} from '../hubspot';
import { chargebeeCustomerUrl, chargebeeSubscriptionUrl } from '../chargebee';
import {
  CfOrder, CfOrderDetail, isTerminal, statusLabel,
} from '../features/cfOrders/cfOrders';
import {
  changeLabel, customerName, eventLabel, formatDay, formatMoment, isLive, leadTime, noteLabel,
  serviceFields, surfaceLabel,
} from '../features/cfOrders/format';
import { statusColour } from './cfOrderStatus';

/** One label and its value. A value nobody has is left out, not shown blank. */
const Field = ({ label, value = null, mono = false }: {
  label: string; value?: React.ReactNode; mono?: boolean;
}) => {
  if (value === undefined || value === null || value === '') return null;
  return (
    <Box sx={{
      display: 'grid', gridTemplateColumns: '150px 1fr', columnGap: 2, py: 0.5,
    }}
    >
      <Typography variant="body2" color="text.secondary">{label}</Typography>
      <Typography
        variant="body2"
        sx={mono
          ? { fontFamily: 'monospace', wordBreak: 'break-all' }
          : { wordBreak: 'break-word' }}
      >
        {value}
      </Typography>
    </Box>
  );
};

const Section = ({ title, children }: { title: string; children: React.ReactNode }) => (
  <Box sx={{ mt: 2 }}>
    <Typography
      variant="overline"
      component="h3"
      color="text.secondary"
      sx={{ letterSpacing: '0.08em', lineHeight: 2 }}
    >
      {title}
    </Typography>
    {children}
  </Box>
);

const External = ({ href, children }: { href: string; children: React.ReactNode }) => (
  <Link href={href} target="_blank" rel="noreferrer">{children}</Link>
);

/** The building id, linked when the backend named the portal's Building type. */
const buildingLink = (row: CfOrder): React.ReactNode => {
  if (!row.hubspot_building_id) return undefined;
  const href = hubspotBuildingUrl(row.hubspot_building_object_type, row.hubspot_building_id);
  return href
    ? <External href={href}>{row.hubspot_building_id}</External>
    : row.hubspot_building_id;
};

export interface CfOrderCardProps {
  /** The row that was clicked, shown straight away while the record loads. */
  row: CfOrder | null;
  detail: CfOrderDetail | null;
  loading: boolean;
  error: string | null;
  cancelling: boolean;
  changing: boolean;
  onClose: () => void;
  onCancel: (row: CfOrder) => void;
  onChange: (row: CfOrder) => void;
}

/**
 * Everything about one Community Fibre order, opened from its row. Laid out
 * for someone chasing a stuck order: where it is, when it was meant to happen,
 * what has happened to it, and only then who and where, and every id.
 */
const CfOrderCard = ({
  row, detail, loading, error, cancelling, changing, onClose, onCancel, onChange,
}: CfOrderCardProps) => {
  const open = row !== null;
  const status = detail?.status ?? row?.status ?? '';
  const theirState = detail?.external_state ?? row?.external_state;
  const reason = detail?.reason ?? row?.reason;
  const email = row?.email ?? detail?.contact?.email;
  const events = [...(detail?.events ?? [])].reverse(); // newest first
  const service = serviceFields(detail?.service ?? row?.service);
  const notes = [...(detail?.wholesaler_notes ?? [])].reverse(); // newest first
  const ticketId = row?.hubspot_ticket_id ?? detail?.hubspot_ticket_id;
  const changes = [...(detail?.changes ?? row?.changes ?? [])].reverse(); // newest first
  // A live service is disconnected, not cancelled.
  const cancelLabel = row && isLive(row) ? 'Disconnect service' : 'Cancel order';

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{ sx: { width: { xs: '100%', sm: 520 } } }}
    >
      {row && (
        <Card elevation={0} sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
          <CardContent sx={{ flexGrow: 1, overflowY: 'auto' }}>
            <Stack direction="row" alignItems="flex-start" spacing={1}>
              <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                <Typography variant="h6" component="h2" sx={{ lineHeight: 1.3 }}>
                  {row.address || detail?.formatted_address || 'No address'}
                </Typography>
                {/* What staff are asked about: their order, and the building. */}
                <Typography variant="body2" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
                  {[row.community_fibre_order_id || 'Not placed', row.building_gnaf_id]
                    .filter(Boolean).join(' · ')}
                </Typography>
              </Box>
              <IconButton aria-label="Close" onClick={onClose} edge="end">
                <CloseIcon />
              </IconButton>
            </Stack>

            <Stack direction="row" spacing={1} sx={{ mt: 1.5 }} alignItems="center">
              <Chip size="small" label={statusLabel(status)} color={statusColour(status)} />
              {theirState && (
                <Typography variant="body2" color="text.secondary">
                  Community Fibre say:
                  {' '}
                  {theirState}
                </Typography>
              )}
            </Stack>

            {reason && <Alert severity="error" sx={{ mt: 2 }}>{reason}</Alert>}
            {status === 'cancel_requested' && (
              <Alert severity="warning" sx={{ mt: 2 }}>
                Cancellation asked for. Community Fibre apply it in their own
                time; this order stays open until they confirm.
              </Alert>
            )}
            {status === 'change_requested' && (
              <Alert severity="warning" sx={{ mt: 2 }}>
                Plan change asked for. Community Fibre apply it in their own
                time; the service stays on its current plan until they confirm.
              </Alert>
            )}
            {status === 'cease_requested' && (
              <Alert severity="warning" sx={{ mt: 2 }}>
                Disconnection asked for. Community Fibre apply it in their own
                time; the service stays connected until they confirm.
              </Alert>
            )}

            {loading && (
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 2 }}>
                <CircularProgress size={18} />
                <Typography variant="body2" color="text.secondary">
                  Loading the full record
                </Typography>
              </Stack>
            )}
            {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}

            {/* The three days apart: asked for, confirmed by Community Fibre
                (which moves, and can be earlier than asked), and connected. */}
            <Section title="Dates">
              <Field label="Asked for" value={formatDay(row.preferred_date)} />
              <Field label="Confirmed for" value={formatDay(row.expected_date)} />
              <Field label="Connected on" value={formatDay(row.completed_date ?? detail?.completed_date)} />
              <Field label="Lead time quoted" value={leadTime(row.installation_days ?? detail?.installation_days)} />
              <Field label="Placed" value={formatMoment(row.created_at)} />
              <Field label="Closed" value={formatMoment(detail?.completed_at)} />
            </Section>

            {service.length > 0 && (
              <Section title="Service as built">
                {service.map((f) => <Field key={f.label} label={f.label} value={f.value} mono />)}
              </Section>
            )}

            {changes.length > 0 && (
              <Section title="Changes to the live service">
                <Box component="ul" sx={{ listStyle: 'none', p: 0, m: 0 }}>
                  {changes.map((c) => (
                    <Box
                      component="li"
                      key={c.id}
                      sx={{ py: 0.75, borderBottom: 1, borderColor: 'divider' }}
                    >
                      <Typography variant="body2">{changeLabel(c)}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {[formatMoment(c.requested_at), c.completed_at && `answered ${formatMoment(c.completed_at)}`]
                          .filter(Boolean).join(' · ')}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </Section>
            )}

            {notes.length > 0 && (
              <Section title="Community Fibre notes">
                <Box component="ul" sx={{ listStyle: 'none', p: 0, m: 0 }}>
                  {notes.map((n) => (
                    <Box
                      component="li"
                      key={n.id ?? `${n.at}-${n.text}`}
                      sx={{ py: 0.75, borderBottom: 1, borderColor: 'divider' }}
                    >
                      <Typography variant="body2">{noteLabel(n)}</Typography>
                    </Box>
                  ))}
                </Box>
              </Section>
            )}

            {events.length > 0 && (
              <Section title="History">
                <Box component="ol" sx={{ listStyle: 'none', p: 0, m: 0 }}>
                  {events.map((e) => (
                    <Box
                      component="li"
                      key={`${e.at}-${e.to}-${e.detail ?? ''}`}
                      sx={{ py: 0.75, borderBottom: 1, borderColor: 'divider' }}
                    >
                      <Typography variant="body2">{eventLabel(e)}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {formatMoment(e.at)}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </Section>
            )}

            <Section title="Customer">
              <Field label="Name" value={row.customer || customerName(detail?.contact)} />
              <Field label="Email" value={email && <Link href={`mailto:${email}`}>{email}</Link>} />
              <Field label="Phone" value={detail?.contact?.phone} />
            </Section>

            <Section title="Location">
              <Field
                label="Building G-NAF"
                value={row.building_gnaf_id ?? detail?.building_gnaf_id}
                mono
              />
              <Field label="Unit G-NAF" value={row.gnaf_id ?? detail?.gnaf_id} mono />
              <Field label="Technology" value={detail?.technology?.toUpperCase()} />
            </Section>

            <Section title="Plan">
              <Field label="Plan" value={row.plan_id} />
              <Field label="Product" value={row.product ?? detail?.external_plan_id} mono />
              <Field label="Access product (OVC)" value={row.access_product ?? detail?.access_plan_id} mono />
              <Field label="Add-ons" value={detail?.add_ons?.join(', ')} />
            </Section>

            <Section title="Ids">
              <Field label="Community Fibre order" value={row.community_fibre_order_id} mono />
              <Field label="Ordered via" value={surfaceLabel(row.order_api ?? detail?.order_api)} />
              <Field label="Our reference" value={row.reference} mono />
              <Field label="Their service" value={row.service_number} mono />
              <Field label="Service id" value={row.service_id} mono />
              <Field
                label="HubSpot deal"
                value={row.hubspot_deal_id && (
                  <External href={hubspotDealUrl(row.hubspot_deal_id)}>
                    {row.hubspot_deal_id}
                  </External>
                )}
              />
              <Field
                label="HubSpot ticket"
                value={ticketId && (
                  <External href={hubspotTicketUrl(ticketId)}>{ticketId}</External>
                )}
              />
              <Field label="HubSpot building" value={buildingLink(row)} mono />
              <Field
                label="HubSpot contact"
                value={detail?.hubspot_contact_id && (
                  <External href={hubspotContactUrl(detail.hubspot_contact_id)}>
                    {detail.hubspot_contact_id}
                  </External>
                )}
              />
              <Field
                label="Chargebee customer"
                value={detail?.cb_customer_id && (
                  <External href={chargebeeCustomerUrl(detail.cb_customer_id)}>
                    {detail.cb_customer_id}
                  </External>
                )}
              />
              <Field
                label="Subscription"
                value={detail?.cb_subscription_id && (
                  <External href={chargebeeSubscriptionUrl(detail.cb_subscription_id)}>
                    {detail.cb_subscription_id}
                  </External>
                )}
              />
              <Field label="Internal id" value={row.order_id} mono />
            </Section>
          </CardContent>

          <Divider />
          <CardActions sx={{ px: 2, py: 1.5, justifyContent: 'space-between' }}>
            <Button variant="outlined" onClick={onClose}>Close</Button>
            <Stack direction="row" spacing={1}>
              {/* A live service can be moved to another plan. */}
              {row.changeable && (
                <Button
                  variant="outlined"
                  disabled={changing || cancelling}
                  onClick={() => onChange(row)}
                >
                  {changing ? 'Asking them…' : 'Change plan'}
                </Button>
              )}
              {/* An order being built can be called off and a live service
                  disconnected; a finished one has nothing to cancel, so no
                  button; one already awaiting an answer says why it cannot
                  be asked twice. */}
              {row.cancellable ? (
                <Button
                  color="error"
                  variant="outlined"
                  disabled={cancelling || changing}
                  onClick={() => onCancel(row)}
                >
                  {cancelling ? 'Asking them…' : cancelLabel}
                </Button>
              ) : !isTerminal(status) && (
                <Tooltip title="A cancellation or a change has already been asked for">
                  <span>
                    <Button color="error" variant="outlined" disabled>{cancelLabel}</Button>
                  </span>
                </Tooltip>
              )}
            </Stack>
          </CardActions>
        </Card>
      )}
    </Drawer>
  );
};

export default CfOrderCard;
